import { Envelope } from "@phosphor-icons/react";
import { Globe, Shield, GitFork, Cpu } from "lucide-react";
import { motion, useScroll, useMotionValueEvent } from "motion/react";
import {
	Suspense,
	lazy,
	useState,
	useRef,
} from "react";
import logoLockup from "@/assets/images/basilica-logo-lockup.svg";
import { BasilicaLogo } from "@/components/basilica-logo";
import { Supporters } from "@/components/supporters";
import { cn } from "@/lib/utils";
import { GlobeSpinner } from "./globe-spinner";


function useScrollDirection(containerRef: React.RefObject<HTMLDivElement | null>) {
	const [hidden, setHidden] = useState(false);
	const { scrollY } = useScroll({ container: containerRef });
	const lastScrollY = useRef(0);

	useMotionValueEvent(scrollY, "change", (latest) => {
		const previous = lastScrollY.current;
		const direction = latest > previous ? "down" : "up";
		if (direction === "down" && latest > 100) {
			setHidden(true);
		} else if (direction === "up") {
			setHidden(false);
		}
		lastScrollY.current = latest;
	});

	return hidden;
}

const Earth = lazy(() => import("./earth"));

function Button({
	children,
	size = "default",
	type = "button",
	disabled = false,
	onClick,
	className,
}: {
	children: React.ReactNode;
	size?: "default" | "large";
	type?: "button" | "submit";
	disabled?: boolean;
	onClick?: () => void;
	className?: string;
}) {
	return (
		<motion.button
			type={type}
			disabled={disabled}
			onClick={onClick}
			className={cn(
				"group relative flex items-center justify-center gap-2.5 rounded-xl",
				"transition-all duration-100",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
				size === "large" ? "px-6 py-3.5 h-[48px]" : "px-4 py-2 h-[40px]",
				disabled && "cursor-not-allowed",
				!disabled && "bg-white/10 hover:bg-white/90",
				disabled && "bg-white/10",
				className
			)}
		>
			<span className={cn(
				"font-sans font-normal transition-colors duration-100",
				size === "large" ? "text-base" : "text-[14px]",
				disabled ? "text-white/40" : "text-white group-hover:text-[#0a0a0a]"
			)}>
				{children}
			</span>
		</motion.button>
	);
}

const PILLARS = [
	{
		icon: Globe,
		title: "Open networks",
		desc: "Data syncs peer\u2011to\u2011peer with no intermediary servers. Users own their work and creators keep full\u00A0control.",
	},
	{
		icon: Shield,
		title: "Secure by design",
		desc: "Verified at every layer with no single point of failure. Trust from mathematics, not terms of\u00A0service.",
	},
	{
		icon: GitFork,
		title: "Contributor economics",
		desc: "Value flows to the people who create it. Developers, artists, and operators rewarded across the\u00A0stack.",
	},
	{
		icon: Cpu,
		title: "AI-native infrastructure",
		desc: "Identity, provenance, and attribution built into the protocol. A foundation for next‑generation creative\u00A0tools.",
	},
];

export default function LandingPageRoute() {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const headerHidden = useScrollDirection(scrollContainerRef);
	const globeContainerRef = useRef<HTMLDivElement>(null);

	return (
		<div ref={scrollContainerRef} className="relative h-full w-full overflow-y-auto overflow-x-hidden bg-[#0a0a0a] text-white pt-5">

			{/* ===== HEADER ===== */}
			<motion.header
				initial={{ y: 0 }}
				animate={{ y: headerHidden ? "-150%" : 0 }}
				transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
				className="sticky top-0 z-40 px-4 md:px-[8%] lg:px-[15%] py-3"
			>
				<div className="relative bg-[#050505]/80 backdrop-blur-xl rounded-xl overflow-hidden">
					<div
						className="pointer-events-none absolute inset-0 opacity-[0.08]"
						style={{
							backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
						}}
					/>
					<nav className="relative flex items-center justify-between px-5 py-3">
						<motion.a
							href="/"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.5 }}
							className="flex items-center"
						>
							<img src={logoLockup} alt="Basilica" className="h-[22px]" />
						</motion.a>

						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.5, delay: 0.1 }}
							className="flex items-center gap-8"
						>
							<a href="/about" className="text-[15px] text-white/60 hover:text-white transition-colors">
								About Us
							</a>
							<Button onClick={() => {
								const el = document.getElementById("contact");
								const container = scrollContainerRef.current;
								if (!el || !container) return;
								const start = container.scrollTop;
								const end = el.offsetTop - container.offsetTop;
								const duration = 400;
								const t0 = performance.now();
								const step = (now: number) => {
									const p = Math.min((now - t0) / duration, 1);
									const ease = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
									container.scrollTop = start + (end - start) * ease;
									if (p < 1) requestAnimationFrame(step);
								};
								requestAnimationFrame(step);
							}}>
								Contact
							</Button>
						</motion.div>
					</nav>
				</div>
			</motion.header>

			{/* ===== HERO ===== */}
			<section className="py-16 md:py-24 px-9 md:px-[8%] lg:px-[15%] overflow-hidden">
				<div className="flex flex-col min-[1470px]:flex-row items-center justify-center gap-8 min-[1470px]:gap-[clamp(64px,6vw,160px)]">
					<div className="min-w-0 text-center min-[1470px]:text-left">
						<motion.h1
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							className="font-sans text-[28px] md:text-[32px] lg:text-[42px] font-normal tracking-tight text-white"
						>
							Hello again, world.
						</motion.h1>

						<motion.p
							initial={{ opacity: 0, y: 15 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.35 }}
							className="mt-5 md:mt-6 max-w-md mx-auto min-[1470px]:mx-0 font-sans text-[17px] md:text-xl leading-relaxed text-white/50"
						>
							Open network infrastructure for the next generation of creative tools, connected applications, and intelligent&nbsp;software.
						</motion.p>
					</div>

					<motion.div
						ref={globeContainerRef}
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
						className="h-[300px] w-[300px] md:h-[380px] md:w-[380px] lg:h-[464px] lg:w-[464px] mx-auto min-[1470px]:mx-0 overflow-hidden flex-shrink-0"
					>
						<div className="origin-top-left scale-[0.6466] md:scale-[0.819] lg:scale-100 h-[464px] w-[464px]">
							<Suspense fallback={<GlobeSpinner />}>
								<Earth
									width={464}
									height={464}
									noBackground
									enablePointerInteraction={true}
									atmosphereAltitude={0.1}
									isPaused={false}
									initialLng={-140}
								/>
							</Suspense>
						</div>
					</motion.div>
				</div>
			</section>

			{/* ===== WHAT WE'RE BUILDING ===== */}
			<section className="relative py-16 md:py-24 px-9 md:px-[8%] lg:px-[15%]">
				{/* Subtle radial glow */}
				<div className="pointer-events-none absolute inset-0 overflow-hidden">
					<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full bg-white/[0.015] blur-[120px]" />
				</div>

				<div className="relative mx-auto max-w-5xl">
					<motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="text-left md:text-center text-[28px] md:text-[32px] lg:text-[42px] font-sans font-normal tracking-tight text-white">
						Tools for what comes&nbsp;next.
					</motion.h2>
					<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }} className="mt-4 md:mt-5 text-left md:text-center text-[17px] md:text-xl text-white/50 max-w-lg md:mx-auto leading-relaxed">
						New infrastructure for creation, collaboration, and connection — starting with the network&nbsp;layer.
					</motion.p>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12 md:mt-14 max-w-[820px] mx-auto">
						{PILLARS.map((item, i) => (
							<motion.div
								key={item.title}
								initial={{ opacity: 0, y: 16 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{ duration: 0.4, delay: i * 0.08, ease: [0.25, 1, 0.5, 1] }}
								className="relative p-5 bg-white/[0.03] border border-white/[0.06] rounded-xl hover:border-white/[0.10] transition-colors duration-300 overflow-hidden"
							>
								<div className="absolute top-0 left-5 right-5 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
								<div className="flex items-start gap-4">
									<div className="shrink-0 p-2 rounded-lg bg-white/[0.04] text-white/50">
										<item.icon size={20} strokeWidth={1.5} />
									</div>
									<div>
										<h3 className="text-[17px] font-medium tracking-tight text-white mb-1.5">{item.title}</h3>
										<p className="text-[15px] text-white/40 leading-relaxed">{item.desc}</p>
									</div>
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* ===== CONTACT ===== */}
			<section id="contact" className="relative py-16 md:py-24 px-9 md:px-[8%] lg:px-[15%]">
				<div className="mx-auto max-w-3xl text-center">
					<motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="text-[28px] md:text-[32px] lg:text-[42px] font-sans font-normal tracking-tight text-white">
						Get in touch.
					</motion.h2>

					<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }} className="mt-4 md:mt-5 text-[17px] md:text-xl text-white/45 max-w-xl mx-auto leading-relaxed">
						We're always looking for great people and interesting problems. We'd love to hear from&nbsp;you.
					</motion.p>

					<motion.a
						href="mailto:contact@basilica.digital"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.3 }}
						className="mt-8 md:mt-10 inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-white/10 hover:bg-white/90 text-white hover:text-[#0a0a0a] transition-all duration-200 text-[17px]"
					>
						<Envelope size={20} />
						contact@basilica.digital
					</motion.a>
				</div>
			</section>

			<Supporters />

			{/* ===== FOOTER ===== */}
			<footer className="relative mt-16 md:mt-24 pt-8 pb-12 md:pb-20 px-9 md:px-[8%] lg:px-[15%] border-t border-white/5">
				<div className="flex flex-col md:flex-row items-center justify-between gap-6">
					<div className="flex items-center gap-2">
						<span className="text-[15px] text-white/40">© 2026 Basilica Digital Inc.</span>
					</div>

					<div className="flex items-center gap-5">
						<a href="/privacy" className="text-[15px] text-white/40 hover:text-white transition-colors duration-150">
							Privacy Policy
						</a>
						<a href="/terms" className="text-[15px] text-white/40 hover:text-white transition-colors duration-150">
							Terms of Use
						</a>
					</div>
				</div>
			</footer>
		</div>
	);
}
